/**
 * SessionComplete — post-interview result card. Embedded in CompletePage
 * and in InterviewView's complete state.
 *
 * Sprint 11.2 redesign: drops the kaiva-teal gradient header for the v1.1
 * page-card language used by SessionSummary. Mono eyebrow + Geist 600
 * headline + stat row + optional highlights + collapsible transcript +
 * footer with Sienna-primary Done.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Download, RotateCw } from 'lucide-react';
import { TranscriptView, type TranscriptViewProps } from './TranscriptView';
import { EmptyHint } from './feedback';

type TranscriptItems = NonNullable<TranscriptViewProps['transcript']>;
type TranscriptItem = TranscriptItems[number];

export interface SessionCompleteProps {
  sessionName?: string;
  durationSeconds?: number | null;
  startedAt?: string | Date | null;
  completedAt?: string | Date | null;
  transcript?: TranscriptViewProps['transcript'];
  highlights?: string[];
  dashboardHref?: string;
  isRestarting?: boolean;
  onStartNewSession?: () => void;
}

export function SessionComplete({
  sessionName = 'Interview Session',
  durationSeconds,
  startedAt,
  completedAt,
  transcript,
  highlights,
  dashboardHref = '/',
  isRestarting = false,
  onStartNewSession,
}: SessionCompleteProps) {
  const entries = useMemo<TranscriptItems>(
    () => (transcript ?? []) as TranscriptItems,
    [transcript],
  );

  const resolvedDurationSeconds = useMemo(() => {
    if (typeof durationSeconds === 'number' && Number.isFinite(durationSeconds)) {
      return Math.max(0, durationSeconds);
    }
    const start = parseDate(startedAt);
    const end = parseDate(completedAt);
    if (!start || !end) return 0;
    return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
  }, [completedAt, durationSeconds, startedAt]);

  const durationLabel = useMemo(
    () => formatDuration(resolvedDurationSeconds),
    [resolvedDurationSeconds],
  );

  const wordCount = useMemo(() => {
    return entries
      .map(getEntryText)
      .filter((t) => t.length > 0)
      .join(' ')
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0).length;
  }, [entries]);

  const turnCount = entries.filter((e) => getEntryText(e).length > 0).length;

  const completedDate = useMemo(() => {
    const end = parseDate(completedAt);
    if (!end) return null;
    return end.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [completedAt]);

  const handleDownloadTranscript = () => {
    if (entries.length === 0) return;
    const text = buildTranscriptText(sessionName, durationLabel, wordCount, entries);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const safeName = sessionName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    anchor.href = url;
    anchor.download = `${safeName || 'session'}-transcript.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      className="overflow-hidden"
      style={{
        background: 'hsl(var(--paper))',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid hsl(var(--hairline-2))',
        /* v1.6: no shadows on cards. Lift comes from Paper-on-Linen contrast. */
      }}
    >
      {/* Header — tonal-surface shift instead of 1px divider */}
      <header
        className="px-6 pb-5 pt-6 sm:px-8"
        style={{ background: 'hsl(var(--off-white-container-high))' }}
      >
        <div
          className="mb-1.5 inline-flex items-center gap-1.5 kv-eyebrow kv-eyebrow--accent"
        >
          <span
            aria-hidden="true"
            className="inline-grid place-items-center rounded-full"
            style={{
              width: 16,
              height: 16,
              background: 'hsl(var(--teal) / 0.18)',
              color: 'hsl(var(--teal-dark))',
            }}
          >
            <Check className="h-2.5 w-2.5" aria-hidden="true" />
          </span>
          Interview · Complete
        </div>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: 'hsl(var(--foreground))',
            letterSpacing: '-0.015em',
            lineHeight: 1.25,
            margin: 0,
          }}
        >
          Captured: {sessionName}
        </h1>
        {(completedDate || resolvedDurationSeconds > 0) && (
          <p
            className="mt-1.5"
            style={{
              fontSize: 11.5,
              color: 'hsl(var(--muted-foreground))',
              letterSpacing: '0.02em',
            }}
          >
            {[completedDate, resolvedDurationSeconds > 0 ? `${durationLabel}` : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </header>

      {/* Body */}
      <div className="flex flex-col gap-6 px-6 py-6 sm:px-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Duration" value={durationLabel} />
          <Stat
            label={`Turn${turnCount === 1 ? '' : 's'}`}
            value={turnCount}
            accent={turnCount >= 10 ? 'teal' : 'default'}
          />
          <Stat label="Words" value={wordCount} />
        </div>

        {/* Highlights */}
        <section>
          <SectionHeading>Highlights</SectionHeading>
          {highlights && highlights.length > 0 ? (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {highlights.slice(0, 5).map((h, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    aria-hidden="true"
                    className="mt-[7px] inline-block flex-shrink-0 rounded-full"
                    style={{
                      width: 6,
                      height: 6,
                      background: 'hsl(var(--sienna))',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      letterSpacing: '-0.003em',
                      color: 'hsl(var(--foreground))',
                    }}
                  >
                    {h}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div
              className="rounded-[12px] py-6"
              style={{
                background: 'hsl(var(--background))',
                border: '1px dashed hsl(var(--border-strong))',
              }}
            >
              <EmptyHint
                title="No highlights yet"
                body="Captured insights will appear here once the session is reviewed."
              />
            </div>
          )}
        </section>

        {/* Transcript (collapsible if many turns) */}
        {entries.length > 0 && (
          <section>
            <CollapsibleTranscript
              entries={entries}
              defaultOpen={turnCount <= 12}
              turnCount={turnCount}
            />
          </section>
        )}
      </div>

      {/* Footer — tonal-surface shift instead of 1px divider */}
      <footer
        className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 sm:px-8"
        style={{ background: 'hsl(var(--off-white-container-high))' }}
      >
        <div className="flex items-center gap-2">
          {onStartNewSession && (
            <button
              type="button"
              onClick={onStartNewSession}
              disabled={isRestarting}
              className="inline-flex items-center gap-1.5 transition-colors hover:[background:hsl(var(--muted))] disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 8,
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
                fontSize: 12.5,
                fontWeight: 500,
              }}
            >
              <RotateCw className="h-3 w-3" aria-hidden="true" />
              {isRestarting ? 'Restarting…' : 'Restart interview'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownloadTranscript}
            disabled={entries.length === 0}
            className="inline-flex items-center gap-1.5 transition-colors hover:[color:hsl(var(--foreground))] disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              height: 32,
              padding: '0 10px',
              borderRadius: 8,
              background: 'transparent',
              color: 'hsl(var(--muted-foreground))',
              border: '1px solid transparent',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <Download className="h-3 w-3" aria-hidden="true" />
            Download transcript
          </button>
          <Link
            to={dashboardHref}
            className="inline-flex items-center gap-1.5 transition-colors"
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 8,
              background: 'hsl(var(--teal))',
              color: 'hsl(var(--slate-navy))',
              border: '1px solid hsl(var(--teal))',
              fontSize: 12.5,
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft className="h-3 w-3" aria-hidden="true" />
            Done
          </Link>
        </div>
      </footer>
    </section>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'teal' | 'persimmon';
}) {
  const valueColor =
    accent === 'teal'
      ? 'hsl(var(--teal-dark))'
      : accent === 'persimmon'
        ? 'hsl(var(--persimmon-deep))'
        : 'hsl(var(--foreground))';
  return (
    <div
      className="rounded-[12px] px-4 py-3.5 text-center"
      style={{
        background: 'hsl(var(--background))',
        border: '1px solid hsl(var(--border-strong))',
      }}
    >
      <div
        className="tabular-nums"
        style={{
          fontSize: 26,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: valueColor,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        className="mt-1.5 kv-eyebrow"
        style={{
          fontSize: 9.5,
          color: 'hsl(var(--muted-foreground))',
        }}
      >
        {label}
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4
      className="m-0 mb-2 kv-eyebrow"
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: 'hsl(var(--muted-foreground))',
      }}
    >
      {children}
    </h4>
  );
}

function CollapsibleTranscript({
  entries,
  defaultOpen,
  turnCount,
}: {
  entries: TranscriptItems;
  defaultOpen: boolean;
  turnCount: number;
}) {
  return (
    <details open={defaultOpen} className="group">
      <summary
        className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-[12px] px-4 py-2.5 transition-colors hover:[background:hsl(var(--background))]"
        style={{
          background: 'hsl(var(--background))',
          border: '1px solid hsl(var(--border-strong))',
        }}
      >
        <span
          className="kv-eyebrow"
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: 'hsl(var(--foreground))',
          }}
        >
          Full transcript · {turnCount} turn{turnCount === 1 ? '' : 's'}
        </span>
        <span
          aria-hidden="true"
          className="kv-eyebrow"
          style={{
            color: 'hsl(var(--persimmon-deep))',
          }}
        >
          <span className="group-open:hidden">Show</span>
          <span className="hidden group-open:inline">Hide</span>
        </span>
      </summary>
      <div className="mt-3">
        <TranscriptView transcript={entries} autoScroll={false} title="Full transcript" />
      </div>
    </details>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  if (h > 0) {
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }
  return `${pad(m)}:${pad(s)}`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getEntryText(entry: TranscriptItem): string {
  if ('text' in entry && typeof entry.text === 'string' && entry.text.trim()) {
    return entry.text.trim();
  }
  if ('message' in entry && typeof entry.message === 'string' && entry.message.trim()) {
    return entry.message.trim();
  }
  if ('transcript' in entry && typeof entry.transcript === 'string' && entry.transcript.trim()) {
    return entry.transcript.trim();
  }
  return '';
}

function getEntrySpeakerLabel(entry: TranscriptItem): 'KAIVA' | 'YOU' | 'SYSTEM' {
  const speaker =
    ('role' in entry && typeof entry.role === 'string' && entry.role) ||
    ('speaker' in entry && typeof entry.speaker === 'string' && entry.speaker) ||
    'system';
  if (speaker === 'assistant') return 'KAIVA';
  if (speaker === 'user') return 'YOU';
  return 'SYSTEM';
}

function getEntryTimestamp(entry: TranscriptItem): string {
  const raw = 'timestamp' in entry ? entry.timestamp : undefined;
  if (!raw) return new Date().toISOString();
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

function buildTranscriptText(
  sessionName: string,
  durationLabel: string,
  wordCount: number,
  entries: TranscriptItems,
): string {
  const header = [
    'Kaiva Interview Transcript',
    `Session: ${sessionName}`,
    `Duration: ${durationLabel}`,
    `Word Count: ${wordCount}`,
    `Generated: ${new Date().toISOString()}`,
    '',
    '---',
    '',
  ].join('\n');

  const body = entries
    .map((entry) => {
      const text = getEntryText(entry);
      if (!text) return '';
      const speaker = getEntrySpeakerLabel(entry);
      const time = new Date(getEntryTimestamp(entry)).toLocaleTimeString();
      return `[${time}] ${speaker}: ${text}`;
    })
    .filter((line) => line.length > 0)
    .join('\n');

  return `${header}${body}`;
}

export default SessionComplete;
